/**
 * Shared ndapi user-sync helpers.
 *
 * Both the Kinde and Clerk user-sync paths need to ensure a user record
 * exists in the product backend (ndapi `POST /v2/users`).  This module
 * centralises the pure functions so the two auth-provider-specific hooks
 * share the same transport logic.
 */

import { readAttributionContext, type AttributionContext } from "@nextday/attribution";

export const NDAPI_BASE = import.meta.env.VITE_PRODUCT_API_BASE_URL || "https://prod.nd-api.com";

// ── Status constants ───────────────────────────────────────────────────

export const USER_SYNC_STATUS = {
  IDLE: "idle",
  CHECKING: "checking",
  EXISTS: "exists",
  NEEDS_NAME: "needs_name",
  CREATING: "creating",
  CREATED: "created",
  ERROR: "error",
} as const;

export type UserSyncStatus = (typeof USER_SYNC_STATUS)[keyof typeof USER_SYNC_STATUS];

export type UserSyncResult = {
  status: UserSyncStatus;
  error?: string;
};

// ── Arg types ──────────────────────────────────────────────────────────

export type SyncArgs = {
  applicationId: string;
  anonymousId: string;
};

export type CreateArgs = SyncArgs & {
  displayName: string;
};

// ── Username generation ────────────────────────────────────────────────

const USERNAME_SUFFIX_LENGTH = 15;
const USERNAME_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";

export const generateRandomUsername = (): string => {
  const randomValues = crypto.getRandomValues(new Uint8Array(USERNAME_SUFFIX_LENGTH));
  const suffix = Array.from(
    randomValues,
    (value) => USERNAME_ALPHABET[value % USERNAME_ALPHABET.length],
  ).join("");
  return `user_${suffix}`;
};

// ── Response introspection ─────────────────────────────────────────────

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const USER_RECORD_FIELDS = ["id", "email", "name", "username"] as const;

const recordHasUserIdentity = (record: Record<string, unknown>): boolean =>
  USER_RECORD_FIELDS.some((field) => {
    const value = record[field];
    return typeof value === "string" && value.trim().length > 0;
  });

export const responseContainsUserRecord = (body: unknown): boolean => {
  if (!isObjectRecord(body)) return false;
  if (recordHasUserIdentity(body)) return true;
  const user = body.user;
  return isObjectRecord(user) && recordHasUserIdentity(user);
};

// ── Attribution ────────────────────────────────────────────────────────

export const safeReadAttributionContext = (): AttributionContext | undefined => {
  try {
    return readAttributionContext();
  } catch {
    return undefined;
  }
};

// ── Display name resolution ────────────────────────────────────────────

const DISPLAY_NAME_FALLBACK_KEYS = ["name", "display_name"] as const;

const readStringAnswer = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

export const resolveDisplayName = (
  answers: Readonly<Record<string, unknown>>,
  nameAnswerKey: string,
): string => {
  const configuredName = readStringAnswer(answers[nameAnswerKey]);
  if (configuredName) return configuredName;

  for (const fallbackKey of DISPLAY_NAME_FALLBACK_KEYS) {
    if (fallbackKey === nameAnswerKey) continue;
    const fallbackName = readStringAnswer(answers[fallbackKey]);
    if (fallbackName) return fallbackName;
  }

  return "";
};

// ── User id namespacing ────────────────────────────────────────────────

/**
 * Applications with their own isolated Kinde org: ndapi stores their users
 * as `<application_id>:kp:123` instead of the raw Kinde id `kp:123` used by
 * the legacy shared apps (spicychat, pixelchat) and partner apps.
 * `POST /v2/users` rejects the request (SC-001-3211) when the body `id`
 * does not match the JWT-derived user id, so the client must apply the same
 * rule. Mirrors `ApplicationUserIdService` in next-platform: the namespace is
 * the `external_org_id` claim of the Kinde access token when present,
 * otherwise the application id for the allowlisted apps.
 */
const NAMESPACED_APPLICATION_IDS: ReadonlySet<string> = new Set(["secretmate"]);
const RAW_ID_APPLICATION_IDS: ReadonlySet<string> = new Set([
  "spicychat",
  "pixelchat",
  "gptgirlfriend",
  "adulttime",
  "evilangel",
  "asgmax",
]);

export const readExternalOrgIdClaim = (token: string): string | undefined => {
  const segment = token.split(".")[1];
  if (!segment) return undefined;
  try {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const payload: unknown = JSON.parse(atob(base64));
    if (typeof payload !== "object" || payload === null) return undefined;
    const claim = (payload as Record<string, unknown>).external_org_id;
    return typeof claim === "string" && claim.trim() !== "" ? claim : undefined;
  } catch {
    return undefined;
  }
};

export const toApplicationUserId = (
  kindeUserId: string,
  applicationId: string,
  externalOrgId?: string,
): string => {
  const appId = applicationId.toLowerCase();
  if (RAW_ID_APPLICATION_IDS.has(appId)) return kindeUserId;
  const namespace =
    externalOrgId?.toLowerCase() ?? (NAMESPACED_APPLICATION_IDS.has(appId) ? appId : undefined);
  if (!namespace) return kindeUserId;
  if (kindeUserId.toLowerCase().startsWith(`${namespace}:`)) return kindeUserId;
  return `${namespace}:${kindeUserId}`;
};

// ── ndapi transport ────────────────────────────────────────────────────

/**
 * GET /v2/users — check whether an authenticated user already has an
 * ndapi-side record.
 */
export const checkNdapiUserExists = async (
  token: string,
  applicationId: string,
  anonymousId: string,
): Promise<boolean> => {
  const res = await fetch(`${NDAPI_BASE}/v2/users`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-app-id": applicationId,
      "x-guest-userid": anonymousId,
      Accept: "application/json",
    },
  });
  if (res.status === 404) return false;
  if (!res.ok) throw new Error(`User check failed: ${res.status}`);
  const body: unknown = await res.json().catch(() => null);
  return responseContainsUserRecord(body);
};

/**
 * POST /v2/users — create (or upsert) the user record in ndapi.
 */
export const createNdapiUser = async (
  token: string,
  applicationId: string,
  anonymousId: string,
  displayName: string,
  user: { id: string; email: string },
  attribution: AttributionContext | undefined,
  username?: string,
): Promise<void> => {
  const affiliateId = attribution?.partners?.affiliateId;
  const tapVid = attribution?.partners?.tapVid;

  const payload = {
    id: toApplicationUserId(user.id, applicationId, readExternalOrgIdClaim(token)),
    email: user.email,
    name: displayName,
    username: username || generateRandomUsername(),
    ...(attribution !== undefined && { attribution }),
    ...(affiliateId && { affiliate_id: affiliateId }),
    ...(tapVid && { tap_tracking_id: tapVid }),
  };

  const res = await fetch(`${NDAPI_BASE}/v2/users`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "x-app-id": applicationId,
      "x-guest-userid": anonymousId,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`User creation failed: ${res.status} ${text}`);
  }
};
